import React, { useContext, useMemo } from 'react';
import { useHistory } from 'react-router';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useData } from 'resift';
import makeValidateMappings from 'fetches/makeValidateMappings';
import MappingProps, * as MappingTypes from 'types/Mapping';
import getSourceHeaderCopy from 'components/Mappings/_helpers/getSourceHeaderCopy';
import MappingsContext from 'helpers/MappingsContext';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Link from '@material-ui/core/Link';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import Button from '@material-ui/core/Button';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableHeadCell from 'components/Table/TableHeadCell';
import TableBody from '@material-ui/core/TableBody';
import OverallValidationStatus from 'components/Mappings/OverallValidationStatus';
import MappingsRow from '../MappingsRow';
import AccordionSectionHeader from 'molecules/AccordionSectionHeader';
import AccordionSectionContent from 'molecules/AccordionSectionContent';

import { HOME_URL, PERSON_PROFILE_URL } from 'components/Router/consts';
import {
  PROFILE_ATTR_MAPPING_TYPE_KEY,
  MAP_ATTRIBUTES_URL,
  REQUIRED_MAPPING_MISSING_ERROR_CODE,
} from 'pages/Directory/consts';

interface Props {
  mappings: MappingProps[];
  isExpanded: boolean;
  onUpdateCard: (newMappings: MappingProps[]) => void;
  onDeleteMapping: (indexToDelete: number) => void;
  onUpdateMapping: (newMappingIndex: number, newMapping: MappingProps) => void;
  onToggleExpansion: () => void;
}

function ProfileAttrMapping({
  mappings,
  isExpanded,
  onUpdateCard,
  onDeleteMapping,
  onUpdateMapping,
  onToggleExpansion,
}: Props) {
  const classes = useStyles();
  const mappingsContext: MappingTypes.MappingContext = useContext(MappingsContext);
  const history = useHistory();
  const { source } = mappingsContext;
  const onSourceCreationWizardMappings = history.location.pathname.includes(MAP_ATTRIBUTES_URL);
  const validateMappings = makeValidateMappings(source.id);
  const validationResponse = useData(validateMappings);
  const validationResults = _get(validationResponse, 'validationResults');

  const handleAddCustomMapping = () => {
    const newMapping = {
      source: '',
      destination: '',
      isRequired: false,
      isUnique: false,
      type: PROFILE_ATTR_MAPPING_TYPE_KEY,
    };

    const newMappings = [...mappings];
    newMappings.unshift({ ...newMapping, isCustom: true });
    onUpdateCard(newMappings);
  };

  const handleUpdateMapping = (newMappingIndex: number, newMapping: any) => {
    const { destination } = newMapping;

    if (destination === 'firstName' || destination === 'lastName') {
      newMapping['isRequired'] = true;
    }

    if (destination === 'email') {
      newMapping['isUnique'] = true;
    }

    onUpdateMapping(newMappingIndex, newMapping);
  };

  const ValidationStatus = useMemo(() => {
    const requiredMappingErrors = !_isEmpty(validationResults)
      ? validationResults.filter(
          (vr: MappingTypes.MappingValidationResult) =>
            vr.code === REQUIRED_MAPPING_MISSING_ERROR_CODE,
        )
      : undefined;

    return (
      <OverallValidationStatus
        mappings={mappings}
        errorsNotTiedToSpecificMappingRows={requiredMappingErrors}
      />
    );
  }, [mappings, validationResults]);

  const renderCardHeader = () => {
    return (
      <CardHeader
        title={
          onSourceCreationWizardMappings
            ? 'Step 3: Add Profile Attributes'
            : 'Add Profile Attributes'
        }
        classes={{ action: classes.action }}
        action={<div>{ValidationStatus}</div>}
        className={classes.cardHeader}
      />
    );
  };

  const renderDetails = () => {
    return (
      <div>
        <CardContent className={classes.cardContent}>
          {_get(source, 'isPrimary') ? (
            <>
              <Typography variant="body2">
                Map properties from your data source to your employee{' '}
                <Link
                  href={`${HOME_URL}${PERSON_PROFILE_URL}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="secondary"
                >
                  profile attributes
                </Link>{' '}
                in Sift. For Primary Sources, we require that you supply{' '}
                <strong>first name, last name,</strong> and <strong>email address</strong> for each
                person, but any additional data will make your Sift profiles that much richer! You
                may add, remove, or change these mappings at any time, and if you have any fields
                that are not mapped to an attribute they will not be imported during your sync.
              </Typography>
              <Typography className={classes.helpTextNote} variant="body2">
                <strong>Note:</strong> If you already mapped any of these attributes (other than
                email) in your primary or hierarchy mapping section, they do not need to be mapped
                here unless you’d like them to be explicitly displayed on profile pages.
              </Typography>
            </>
          ) : (
            <Typography variant="body2">
              Map properties from your data source to your employee profile attributes in Sift. You
              may add, remove, or change these mappings at any time, and you are not required to map
              all properties to Sift.
            </Typography>
          )}

          <div className={classes.addMappingButtonContainer}>
            <Button color="secondary" onClick={handleAddCustomMapping}>
              + Add Mapping
            </Button>
          </div>

          <Table>
            <colgroup>
              <col />
              <col />
              <col style={{ width: '30%' }} />
              <col />
            </colgroup>
            <TableHead>
              <TableHeadCell> {getSourceHeaderCopy(source.type)} </TableHeadCell>
              <TableHeadCell> Preview Data </TableHeadCell>
              <TableHeadCell> Sift Attribute Destination </TableHeadCell>
              <TableHeadCell />
              <TableHeadCell />
            </TableHead>
            <TableBody>
              {mappings.map((mapping, index) => {
                return (
                  <MappingsRow
                    key={index}
                    mapping={mapping}
                    rowIndex={index}
                    onDeleteMapping={onDeleteMapping}
                    onUpdateMapping={handleUpdateMapping}
                  />
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </div>
    );
  };

  return (
    <>
      {!onSourceCreationWizardMappings ? (
        <>
          {renderCardHeader()}
          {renderDetails()}
        </>
      ) : (
        <Accordion expanded={isExpanded} onChange={onToggleExpansion} className={classes.accordion}>
          <AccordionSectionHeader>{renderCardHeader()}</AccordionSectionHeader>
          <AccordionSectionContent>{renderDetails()}</AccordionSectionContent>
        </Accordion>
      )}
    </>
  );
}

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {
    width: '100%',
  },
  accordion: {
    padding: 0,
    margin: 0,
  },
  addMappingButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: theme.spacing(2.5),
    marginTop: theme.spacing(),
  },
  cardContent: {
    paddingTop: 0,
  },
  action: {
    margin: 0,
    alignSelf: 'center',
  },
  helpTextNote: {
    marginTop: theme.spacing(3),
  },
}));

export default ProfileAttrMapping;
